import { useEffect, useState } from "react";
import { toast } from "sonner";
import { fmtErr, uploadFile } from "../api";

// OFFLINE PHOTO QUEUE — when a field upload fails for lack of signal the file is kept in IndexedDB on the phone
// and uploaded automatically when the connection is back (online event, tab visible, or every minute).
const DB = "traide-offline", STORE = "photos", MAX_ATTEMPTS = 6;
const listeners = new Set();
let flushing = false;

const openDb = () => new Promise((res, rej) => { const r = indexedDB.open(DB, 1); r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: "id" }); r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });
async function tx(mode, fn) {
  const db = await openDb();
  return new Promise((res, rej) => { const t = db.transaction(STORE, mode); const req = fn(t.objectStore(STORE)); t.oncomplete = () => { db.close(); res(req?.result); }; t.onerror = () => { db.close(); rej(t.error); }; });
}
const emit = () => listeners.forEach((fn) => { try { fn(); } catch { /* listener gone */ } });

export const isNetworkError = (e) => typeof navigator !== "undefined" && (!navigator.onLine || (!e?.response && (e?.code === "ERR_NETWORK" || e?.code === "ECONNABORTED" || /network|failed to fetch|timeout/i.test(e?.message || ""))));

export async function enqueuePhoto(file, fields, meta = {}) {
  const id = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  await tx("readwrite", (s) => s.put({ id, blob: file, name: file.name, type: file.type, size: file.size, fields, meta, at: Date.now(), attempts: 0, error: null }));
  emit();
  return id;
}
export const listQueued = () => tx("readonly", (s) => s.getAll()).then((r) => (r || []).sort((a, b) => a.at - b.at));
export const removeQueued = async (id) => { await tx("readwrite", (s) => s.delete(id)); emit(); };

export async function flushQueue({ silent = false } = {}) {
  if (flushing || typeof navigator === "undefined" || !navigator.onLine) return { uploaded: 0, failed: 0, done: [] };
  flushing = true;
  const done = []; let failed = 0;
  try {
    for (const it of await listQueued()) {
      try {
        const res = await uploadFile(new File([it.blob], it.name, { type: it.type }), it.fields);
        await tx("readwrite", (s) => s.delete(it.id));
        done.push({ item: it, file: res });
      } catch (e) {
        failed++;
        if (isNetworkError(e)) break; // still offline — try again later, keep order
        const attempts = (it.attempts || 0) + 1;
        if (attempts >= MAX_ATTEMPTS) { await tx("readwrite", (s) => s.delete(it.id)); if (!silent) toast.error(`Couldn't upload ${it.name}: ${fmtErr(e)}`); }
        else await tx("readwrite", (s) => s.put({ ...it, attempts, error: fmtErr(e) }));
      }
    }
  } finally { flushing = false; emit(); }
  if (done.length && !silent) toast.success(`${done.length} photo${done.length > 1 ? "s" : ""} from your phone uploaded${done[0].item.meta?.job_number ? ` to ${done[0].item.meta.job_number}` : ""}`);
  if (done.length) window.dispatchEvent(new CustomEvent("traide:queue-flushed", { detail: done }));
  return { uploaded: done.length, failed, done };
}

export function startQueueSync() {
  if (typeof window === "undefined" || !("indexedDB" in window)) return () => {};
  const online = () => flushQueue();
  const vis = () => document.visibilityState === "visible" && flushQueue({ silent: true });
  window.addEventListener("online", online);
  document.addEventListener("visibilitychange", vis);
  const iv = setInterval(() => flushQueue({ silent: true }), 60000);
  flushQueue({ silent: true });
  return () => { window.removeEventListener("online", online); document.removeEventListener("visibilitychange", vis); clearInterval(iv); };
}

export function useOfflineQueue() {
  const [items, setItems] = useState([]);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const refresh = () => listQueued().then(setItems).catch(() => setItems([]));
    listeners.add(refresh); refresh();
    const on = () => { setOnline(true); refresh(); }, off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { listeners.delete(refresh); window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return { items, count: items.length, online, flush: () => flushQueue() };
}
